/**
 * chunk 加载失败兜底：页面已经坏了，重载一次让它收敛到当前部署的版本。
 *
 * 为什么这是必需品而不是锦上添花：`replicas: 1` + 默认 RollingUpdate 意味着
 * maxSurge=1 / maxUnavailable=0 —— 发布期间**必然**有两个 pod 同时存活、各服务不同版本，
 * Service 随机分发。任何"页面还停在旧版本"的浏览器，只要点一次导航去加载懒路由 chunk，
 * 就有概率打到新 pod 拿到 404。在自动刷新把用户带到新版本之前，这条兜底是唯一的出路。
 *
 * 为什么不用 `vite:preloadError`：Vite 3.2.11 没有这个事件（`__vitePreload` 只对 CSS 拒绝
 * 派发）。所以三个落点全部覆盖：动态 import 的 promise、router.onError、资源级 404。
 */

import type { Router } from 'vue-router';

import { getAutoRefreshSetting } from '/@/settings/autoRefreshSetting';
import {
  evaluateChunkReloadPermission,
  recordChunkReloadAttempt,
} from '/@/utils/autoRefresh/reloadLedger';
import { prepareForReload, reloadNow } from '/@/utils/autoRefresh/prepareReload';

const LOG = '[auto-refresh]';

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i, // Chrome / Edge
  /error loading dynamically imported module/i, // Firefox / Safari
  /Importing a module script failed/i,
  /Unable to preload CSS for/i, // __vitePreload 的显式拒绝
  /Loading chunk \d+ failed/i, // 旧构建/兼容写法
];

let rescuing = false;

function isChunkLoadError(input: unknown): boolean {
  const message = typeof input === 'string' ? input : (input as Error)?.message ?? '';
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(String(message)));
}

function rescue(trigger: string): void {
  if (rescuing) return;

  const cfg = getAutoRefreshSetting();
  const now = Date.now();
  const gate = evaluateChunkReloadPermission(cfg, now);

  if (!gate.allowed) {
    if (gate.backoff) {
      // 注意：**不写版本刷新的退避**。两个账本必须各自独立——chunk 救援次数用尽是它自己的事，
      // 若顺手把版本刷新的轮询也降频 30 分钟，就变成"一个坏掉的资源拖慢了版本更新"。
      console.error(
        `${LOG} 资源加载失败重载次数已用尽，本会话停止救援：${gate.reason}` +
          `（如需继续验证可执行 __autoRefresh.reset()）`,
      );
    }
    return;
  }

  rescuing = true;
  recordChunkReloadAttempt(now);
  console.warn(`${LOG} 检测到资源加载失败（${trigger}），重载页面以收敛到线上版本`);

  // 不走空闲判定：页面已经坏了，等空闲没有意义。但仍然要停语音、关弹窗，
  // 且次数与冷却由 chunk 自己的账本限制，避免持续 404 造成刷新循环。
  prepareForReload(false);
  reloadNow();
}

export function setupChunkErrorHandler(router: Router): void {
  if (!__AUTO_REFRESH_ENABLED__) return;

  // ① 动态 import 的 promise 拒绝 —— 懒路由最常见路径
  window.addEventListener(
    'unhandledrejection',
    (event) => {
      if (isChunkLoadError((event as PromiseRejectionEvent).reason)) {
        rescue('unhandledrejection');
      }
    },
    true,
  );

  // ② Vue Router 捕获的导航错误 —— import() 失败时它可能先于 unhandledrejection 触发
  router.onError((error) => {
    if (isChunkLoadError(error)) {
      rescue('router.onError');
    }
  });

  // ③ 入口 / 懒加载脚本本身加载失败（例如发布后 index.html 引用的 chunk 404，页面直接白屏）。
  //    这类失败不经过 import()，只有捕获期的资源 error 事件能看到。
  //
  //    刻意**只认 SCRIPT，不看 LINK**：
  //    - CSS 预加载失败会由 Vite 的 __vitePreload 显式拒绝（"Unable to preload CSS for ..."），
  //      上面 ①/② 的模式列表已经覆盖，不需要重复监听；
  //    - 而不伴随 import() 失败的纯 <link> 失败，并不能说明"页面是旧版本"（可能只是网络抖动、
  //      静态服务器异常、企业加密软件干扰等），却会消耗掉救援次数。实测中一次本地静态服务器的
  //      CSS 传输异常就把两次机会用光，之后真正遇到旧 chunk 404 时已无救援可用。
  window.addEventListener(
    'error',
    (event) => {
      const target = event.target as HTMLElement | null;
      // 运行时 JS 错误的 target 是 window 自身，没有 tagName，直接跳过
      if (!target || target.tagName !== 'SCRIPT') return;

      const url = (target as HTMLScriptElement).src || '';
      if (url.includes('/assets/')) {
        rescue('resource:SCRIPT');
      }
    },
    true,
  );
}
